// ==============================
// Client "Services" tab — shown inside the customer profile dialog. Renders all
// registrations by default and can be filtered to the latest non-unlimited
// registration, plus a row of the four program levels (School of Fish / Flying
// Fish / Adult / Private). Only the SOF level is mapped to a real badge for now.
// Backed by GET /user/family-services?ids=<userId>.
// ==============================

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { apiGet, apiPatch } from '../../utils/apiClient';
import { notifyAPIClient } from '../../utils/toast';
import CoursePackageTimeline, {
  type TimelinePackage,
} from '../CoursePackageTimeline';
import SofLevelChip from '../common/SofLevelChip';

type ServiceReg = {
  id: number;
  serviceType: string;
  subtype: string;
  serviceInvoiceName: string;
  courseLevel: string;
  visitType: string;
  visitBalance: number;
  sos: string;
  eos: string;
  createdAt: string;
};

type FamilyServiceMember = {
  userId: number;
  registrations: ServiceReg[];
};

type FamilyServicesResponse = {
  data: FamilyServiceMember[];
};

type ServiceUpdateResponse = {
  status: number;
  message: string;
  data: ServiceReg;
};

const isSof = (value: string | null | undefined) =>
  (value ?? '').trim().toLowerCase() === 'sof';

const isUnlimitedService = (value: string | null | undefined) =>
  (value ?? '').trim().toLowerCase() === 'unlimited';

// The four program "level" slots. Only SOF is wired to a real badge for now.
const PROGRAM_SLOTS: { key: string; label: string; mapped: boolean }[] = [
  { key: 'sof', label: 'School of Fish', mapped: true },
  { key: 'ff', label: 'Flying Fish', mapped: false },
  { key: 'adult', label: 'Adult', mapped: false },
  { key: 'private', label: 'Private', mapped: false },
];

type Props = { userId: number | null | undefined };

const ClientServicesTab = ({ userId }: Props) => {
  const [registrations, setRegistrations] = useState<ServiceReg[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState<ServiceReg | null>(
    null,
  );
  const [remainingSessions, setRemainingSessions] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showLatestFiniteOnly, setShowLatestFiniteOnly] = useState(false);

  useEffect(() => {
    if (!userId) {
      setRegistrations([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await apiGet<FamilyServicesResponse>(
          `/user/family-services?ids=${userId}`,
        );
        if (cancelled) return;
        setRegistrations(res.data[0]?.registrations ?? []);
      } catch {
        if (!cancelled) setRegistrations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Current SOF level = the level of the most recent SOF registration.
  const sofLevel = useMemo(() => {
    const sofRegs = registrations
      .filter((reg) => isSof(reg.subtype) || isSof(reg.serviceType))
      .filter((reg) => (reg.courseLevel ?? '').trim() !== '')
      .sort((a, b) => new Date(b.sos).getTime() - new Date(a.sos).getTime());
    return sofRegs[0]?.courseLevel ?? '';
  }, [registrations]);

  const latestFiniteRegistrations = useMemo(
    () =>
      registrations
        .filter((reg) => !isUnlimitedService(reg.visitType))
        .sort((a, b) => {
          const createdAtDifference =
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          return createdAtDifference || b.id - a.id;
        })
        .slice(0, 1),
    [registrations],
  );

  const timelineRegistrations = showLatestFiniteOnly
    ? latestFiniteRegistrations
    : registrations;

  const timelinePackages: TimelinePackage[] = useMemo(
    () =>
      timelineRegistrations.map((reg) => ({
        id: reg.id,
        course:
          reg.serviceInvoiceName?.trim() ||
          `${reg.serviceType} · ${reg.subtype}`.trim(),
        registrationStartDate: reg.sos,
        registrationEndDate: reg.eos,
        serviceType: reg.serviceType,
        subtype: reg.subtype,
        serviceInvoiceName: reg.serviceInvoiceName,
        courseLevel: reg.courseLevel,
        visitType: reg.visitType,
        visitBalance: reg.visitBalance,
      })),
    [timelineRegistrations],
  );

  const openService = (pkg: TimelinePackage) => {
    const registration = registrations.find((item) => item.id === pkg.id);
    if (!registration) return;
    setSelectedService(registration);
    setRemainingSessions(String(registration.visitBalance ?? ''));
    setSaveError('');
  };

  const closeService = () => {
    if (!saving) {
      setSelectedService(null);
      setSaveError('');
    }
  };

  const handleSaveService = async () => {
    if (!selectedService) return;

    const value = Number(remainingSessions);
    if (
      remainingSessions.trim() === '' ||
      !Number.isInteger(value) ||
      value < 0
    ) {
      setSaveError('Remaining sessions must be a non-negative whole number.');
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      const response = await apiPatch<
        ServiceUpdateResponse,
        { remainingSessions: number }
      >(`/user/family-services/registrations/${selectedService.id}`, {
        remainingSessions: value,
      });
      setRegistrations((current) =>
        current.map((item) =>
          item.id === response.data.id ? response.data : item,
        ),
      );
      notifyAPIClient(response.message);
      setSelectedService(null);
    } catch {
      setSaveError('Unable to update this service. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1 }}>
      {/* Program levels */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Program levels
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(4, 1fr)',
          },
          gap: 1.5,
          mb: 3,
        }}
      >
        {PROGRAM_SLOTS.map((slot) => (
          <Box
            key={slot.key}
            sx={(theme) => ({
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: theme.palette.background.default,
              textAlign: 'center',
            })}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.75 }}
            >
              {slot.label}
            </Typography>
            {slot.key === 'sof' ? (
              sofLevel ? (
                <Stack alignItems="center">
                  <SofLevelChip level={sofLevel} serviceType="sof" />
                </Stack>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  No level yet
                </Typography>
              )
            ) : (
              <Typography variant="body2" color="text.disabled">
                Coming soon
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {/* Registrations timeline */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 0.5 }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          Registrations timeline
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showLatestFiniteOnly}
              onChange={(event) =>
                setShowLatestFiniteOnly(event.target.checked)
              }
            />
          }
          label="Latest non-unlimited service only"
          sx={{ mr: 0 }}
        />
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1 }}
      >
        {showLatestFiniteOnly
          ? 'Showing the latest service package with remaining sessions.'
          : 'Showing all registered service packages.'}
      </Typography>
      {timelinePackages.length > 0 ? (
        <CoursePackageTimeline
          packages={timelinePackages}
          onPackageClick={openService}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {showLatestFiniteOnly
            ? 'No non-unlimited service packages found.'
            : 'No registrations yet.'}
        </Typography>
      )}

      <Dialog
        open={Boolean(selectedService)}
        onClose={closeService}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Service information</DialogTitle>
        <DialogContent dividers>
          {selectedService && (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Service
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedService.serviceInvoiceName ||
                    `${selectedService.serviceType} · ${selectedService.subtype}`}
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="body2">
                    {selectedService.serviceType || '—'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Subtype
                  </Typography>
                  <Typography variant="body2">
                    {selectedService.subtype || '—'}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Start date
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedService.sos).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    End date
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedService.eos).toLocaleDateString()}
                  </Typography>
                </Box>
              </Stack>
              {selectedService.visitType.trim().toLowerCase() === 'count' ? (
                <TextField
                  autoFocus
                  fullWidth
                  label="Remaining sessions"
                  type="number"
                  value={remainingSessions}
                  onChange={(event) => setRemainingSessions(event.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  error={Boolean(saveError)}
                  helperText={
                    saveError || 'Enter the number of sessions still available.'
                  }
                  disabled={saving}
                />
              ) : (
                <Alert severity="info">
                  This service is unlimited and does not have remaining
                  sessions.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeService} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveService}
            disabled={
              saving ||
              selectedService?.visitType.trim().toLowerCase() !== 'count'
            }
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientServicesTab;
